import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  CargoProblemWorkspaceCapabilities,
  CargoProblemWorkspaceMessageItem,
} from '@magimp/shared';
import { Button, EmptyState, ErrorState, LoadingState } from '../../../ui/index.js';
import { type AppLocale } from '../../../i18n/index.js';
import { useCargoProblemWorkspaceStream } from '../queries.js';
import {
  cargoProblemStreamKey,
  cargoProblemStreamMessages,
  findCargoProblemMaterialSource,
  mergeCargoProblemStream,
} from '../streamItems.js';
import { useCargoProblemReadTracking } from '../useCargoProblemReadTracking.js';
import { CargoProblemMessageComposer } from './CargoProblemMessageComposer.js';
import { CargoProblemMessageRow } from './CargoProblemMessageRow.js';
import { CargoProblemSystemCard } from './CargoProblemSystemCard.js';

export function CargoProblemConversation({
  problemId,
  meId,
  capabilities,
  unreadCount,
  visible,
  jumpToFileId,
  onJumpResolved,
}: {
  problemId: string;
  meId: string | null;
  capabilities: CargoProblemWorkspaceCapabilities;
  unreadCount: number;
  /**
   * False while the narrow layout keeps this pane behind the material tab. The
   * pane stays mounted so the draft survives the detour, but nothing behind a
   * tab was read.
   */
  visible: boolean;
  /** A material picked in the library; the stream scrolls to the row that published it. */
  jumpToFileId: string | null;
  onJumpResolved: () => void;
}) {
  const { t, i18n } = useTranslation('cargoProblems');
  const locale = i18n.language as AppLocale;
  const stream = useCargoProblemWorkspaceStream(problemId);
  const scrollRef = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<number | null>(null);
  const initiallyPositionedProblemRef = useRef<string | null>(null);
  const [replyTo, setReplyTo] = useState<CargoProblemWorkspaceMessageItem | null>(null);

  const items = useMemo(() => mergeCargoProblemStream(stream.data?.pages ?? []), [stream.data]);
  const messages = useMemo(() => cargoProblemStreamMessages(items), [items]);

  useCargoProblemReadTracking({
    problemId,
    scrollRef,
    messages,
    meId,
    enabled: unreadCount > 0 && visible,
  });

  useEffect(() => {
    const node = scrollRef.current;
    if (
      !node ||
      !visible ||
      !stream.data ||
      stream.isFetchingNextPage ||
      initiallyPositionedProblemRef.current === problemId
    ) {
      return;
    }
    // WHY: a library jump owns the entry position; recording it as handled also
    // prevents a later prepend from replacing either that jump or its anchor.
    initiallyPositionedProblemRef.current = problemId;
    if (!jumpToFileId) node.scrollTop = node.scrollHeight - node.clientHeight;
  }, [jumpToFileId, problemId, stream.data, stream.isFetchingNextPage, visible]);

  // WHY: prepending an older page grows the scroll content above the reader.
  // Restoring the delta keeps the message they were reading under the cursor
  // instead of throwing them up the thread.
  useEffect(() => {
    const node = scrollRef.current;
    const anchor = anchorRef.current;
    if (!node || anchor === null || stream.isFetchingNextPage) return;
    node.scrollTop += node.scrollHeight - anchor;
    anchorRef.current = null;
  }, [items, stream.isFetchingNextPage]);

  useEffect(() => {
    if (!jumpToFileId) return;
    // WHY: the jump can arrive with the pane, before the conversation itself has
    // loaded. Resolving against zero loaded rows would drop a jump whose source
    // is about to render.
    if (!stream.data || stream.isFetchingNextPage) return;
    const key = findCargoProblemMaterialSource(items, jumpToFileId);
    if (key) {
      scrollRef.current
        ?.querySelector(`[data-stream-key="${key}"]`)
        ?.scrollIntoView({ block: 'center' });
      onJumpResolved();
      return;
    }
    // WHY: the source can sit any distance back, so the walk is not capped —
    // every material the library offers was published by a row of this thread,
    // and a brief file never reaches here at all.
    if (stream.hasNextPage) {
      void stream.fetchNextPage();
      return;
    }
    onJumpResolved();
  }, [items, jumpToFileId, onJumpResolved, stream]);

  function loadOlder() {
    anchorRef.current = scrollRef.current?.scrollHeight ?? null;
    void stream.fetchNextPage();
  }

  return (
    <section className="cargo-problem-conversation" aria-label={t('workspace.conversation')}>
      <div className="cargo-problem-conversation-scroll" ref={scrollRef}>
        {stream.isPending ? <LoadingState /> : null}
        {stream.isError ? (
          <ErrorState
            text={t('workspace.conversationError')}
            retryLabel={t('workspace.retry')}
            onRetry={() => void stream.refetch()}
          />
        ) : null}
        {!stream.isPending && !stream.isError && items.length === 0 ? (
          <EmptyState icon="message-square" title={t('workspace.empty')} />
        ) : null}
        {stream.hasNextPage ? (
          <div className="cargo-problem-conversation-older">
            <Button kind="quiet" size="sm" disabled={stream.isFetchingNextPage} onClick={loadOlder}>
              {t('workspace.loadOlder')}
            </Button>
          </div>
        ) : null}
        <ol className="cargo-problem-stream">
          {items.map((item) => (
            <li
              key={cargoProblemStreamKey(item)}
              data-testid="cargo-problem-stream-item"
              data-stream-key={cargoProblemStreamKey(item)}
              {...(item.type === 'MESSAGE' ? { 'data-cargo-problem-message-id': item.id } : {})}
            >
              {item.type === 'MESSAGE' ? (
                <CargoProblemMessageRow
                  problemId={problemId}
                  item={item}
                  mine={item.author.id === meId}
                  locale={locale}
                  canReply={capabilities.canReply}
                  onReply={setReplyTo}
                />
              ) : (
                <CargoProblemSystemCard problemId={problemId} item={item} locale={locale} />
              )}
            </li>
          ))}
        </ol>
      </div>
      <CargoProblemMessageComposer
        problemId={problemId}
        canPost={capabilities.canPostMessage}
        canAttach={capabilities.canAttach}
        replyTo={replyTo}
        onCancelReply={() => setReplyTo(null)}
      />
    </section>
  );
}
